import { useEffect, useState } from 'react';

import { Box, Stack, Fade, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ActionButton, SkopeiConnectLogo } from '~components';
import { StatusVariant } from '~enums';
import { Dimens } from '~themes';

interface OtpInputOverlayProps {
	show: boolean;
	email: string;
	otpLength: number;
	isLoading: boolean;
	error?: string;
	onVerify: (code: string) => Promise<void>;
	onGoBack: () => void;
	onExited: () => void;
	onResend: () => void;
}

/**
 * Overlay component for the user to enter the otp they received in their email for signing in.
 */
const OtpInputOverlay = ({
	show,
	email,
	otpLength,
	isLoading,
	error = '',
	onVerify,
	onGoBack,
	onExited,
	onResend,
}: OtpInputOverlayProps) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const [codeInput, setCodeInput] = useState('');
	const halfOtpLength = otpLength / 2;

	useEffect(() => {
		const verify = async () => {
			if (isLoading) return;

			if (codeInput.length >= otpLength) {
				await onVerify(codeInput);

				setCodeInput('');
			}
		};

		verify();
	}, [codeInput, isLoading, otpLength, onVerify]);

	useEffect(() => {
		if (show) {
			const input = document.getElementById('focusInput');
			input?.focus();
		}
	}, [show]);

	const handleAnimationExited = () => {
		onExited();
		setCodeInput('');
	};

	return (
		<Fade in={show} appear={false} unmountOnExit onExited={handleAnimationExited}>
			<Box
				id='root'
				sx={{
					display: 'flex',
					justifyContent: 'center',
					background: palette.bgPrimary.main,
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
				}}
			>
				<Stack justifyContent='center' alignItems='center'>
					<Box sx={{ lineHeight: 0 }}>
						<SkopeiConnectLogo showLabel={false} size={60} />
					</Box>
					<Typography
						variant='headingLg'
						textAlign='center'
						color={palette.textPrimary.main}
						sx={{ mt: Dimens.spacing3xl }}
					>
						{t('checkYourEmail', { ns: 'auth' })}
					</Typography>
					<Typography
						variant='paragraph'
						textAlign='center'
						color={palette.textSecondary.main}
						sx={{ mt: Dimens.spacingMd }}
					>
						{t('enterNCode', { ns: 'auth', value: otpLength.toString() })}
					</Typography>
					<Typography variant='label' color={palette.textPrimary.main} sx={{ fontWeight: 500 }}>
						{email}
					</Typography>
					<Stack
						direction={{
							xs: 'column',
							sm: 'row',
						}}
						alignItems='center'
						gap={Dimens.spacingLg}
						sx={{ mt: Dimens.spacing4xl, position: 'relative' }}
						onClick={() => {
							const inputElement = document.getElementById('focusInput') as
								| HTMLInputElement
								| undefined;
							if (inputElement) {
								inputElement.focus();

								// Move caret to the end of the current value
								const length = inputElement.value.length;
								inputElement.setSelectionRange(length, length);
							}
						}}
					>
						<Stack direction='row' gap={Dimens.spacingLg}>
							{[...Array(halfOtpLength).keys()].map((key) => (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										width: '52px',
										height: '60px',
										border: '1.5px solid',
										borderColor:
											codeInput.length >= key ?
												palette.borderPrimarySolid.main
											:	palette.borderSecondary.main,
										borderRadius: Dimens.radiusMd,
									}}
									key={key}
									onChange={() => {}}
								>
									<Typography
										variant='headingSm'
										color={
											codeInput.length > key ?
												palette.textPrimary.main
											:	palette.textQuaternary.main
										}
									>
										{codeInput[key] ?? '0'}
									</Typography>
								</Box>
							))}
						</Stack>
						<Box
							sx={{
								width: Dimens.spacingMd,
								height: Dimens.spacingNone,
								border: `1px solid ${palette.bgPrimarySolid.main}`,
								borderRadius: Dimens.radiusFull,
							}}
						></Box>
						<Stack direction='row' gap={Dimens.spacingLg}>
							{[...Array(halfOtpLength).keys()].map((key) => (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										width: '52px',
										height: '60px',
										border: '1.4px solid',
										borderColor:
											codeInput.length >= halfOtpLength + key ?
												palette.borderPrimarySolid.main
											:	palette.borderSecondary.main,
										borderRadius: Dimens.radiusMd,
									}}
									key={key}
									onChange={() => {}}
								>
									<Typography
										variant='headingSm'
										color={
											codeInput.length > halfOtpLength + key ?
												palette.textPrimary.main
											:	palette.textQuaternary.main
										}
									>
										{codeInput[halfOtpLength + key] ?? '0'}
									</Typography>
								</Box>
							))}
						</Stack>
						<Box
							sx={{
								position: 'absolute',
								width: '100%',
								height: '100%',
								'& input:focus-visible': {
									border: 'none',
									outline: 'none',
								},
							}}
						>
							<input
								id='focusInput'
								type='text'
								inputMode='numeric'
								value={codeInput}
								style={{
									width: '100%',
									height: '100%',
									textAlign: 'center',
									caretColor: 'transparent',
									color: 'black',
									border: 'none',
									fontSize: 1,
									background: 'transparent',
								}}
								onChange={(e) => setCodeInput(e.target.value.replaceAll(' ', ''))}
							/>
						</Box>
					</Stack>
					<Fade in={error.length > 0} appear={false}>
						<Typography
							variant='paragraph'
							color={palette.textError.main}
							textAlign='center'
							sx={{ mt: Dimens.spacingXs }}
						>
							{/* Falls back to 'error' because otherwise the component wont get any height */}
							{error.length > 0 ? error : 'error'}
						</Typography>
					</Fade>
					<Stack
						direction='row'
						width={'100%'}
						gap={Dimens.spacingLg}
						sx={{ mt: Dimens.spacingLg }}
					>
						<ActionButton
							label={t('goBack', { ns: 'actions' })}
							loading={isLoading}
							onClick={onGoBack}
							sx={{ width: '100%' }}
						/>
						<ActionButton
							label={t('verify', { ns: 'actions' })}
							loading={isLoading}
							variant={StatusVariant.Brand}
							disabled={codeInput.length < otpLength}
							onClick={() => {
								onVerify(codeInput);
							}}
							sx={{ width: '100%' }}
						/>
					</Stack>
					<Stack
						direction={{
							xs: 'column',
							sm: 'row',
						}}
						alignItems='center'
						justifyContent='center'
						gap={Dimens.spacingXs}
						sx={{ mt: Dimens.spacingXl }}
					>
						<Typography variant='paragraph' color={palette.textSecondary.main}>
							{t('didntReceiveEmail', { ns: 'auth' })}
						</Typography>

						<Typography
							variant='paragraph'
							color={palette.textPrimary.main}
							sx={{
								fontWeight: 500,
								cursor: 'pointer',
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
							onClick={onResend}
						>
							{t('clickToResend', { ns: 'auth' })}
						</Typography>
					</Stack>
				</Stack>
			</Box>
		</Fade>
	);
};

export default OtpInputOverlay;
